import { Disclosure, Transition } from "@headlessui/react";
import { ChevronUpIcon } from "@heroicons/react/outline";
import clsx from "clsx";
import React, { useEffect } from "react";
import { unitPower } from "utils";

interface CollapseProps {
  headerTitle: React.ReactNode;
  children: React.ReactNode;
  closeTitle?: string;
  headerClassName?: string;
  defaultClose?: boolean;
  containerClassName?: string;
  className?: string;
  headerValue?: string | number;
  unitValue?: string | number;
  onOpen?: (state: boolean) => void;
  disable?: boolean;
  isOpen?: boolean;
  arrowClassName?: string;
}

const Collapse = React.forwardRef(
  (
    {
      headerTitle,
      children,
      closeTitle,
      headerClassName,
      defaultClose,
      containerClassName,
      className,
      headerValue,
      unitValue,
      ...props
    }: CollapseProps,
    ref: React.Ref<HTMLButtonElement>
  ) => {
    const [open, setOpen] = React.useState(!defaultClose);

    React.useEffect(() => {
      props.onOpen?.(open);
    }, [open]);

    return (
      <Disclosure defaultOpen={!defaultClose}>
        {({ open }) => {
          useEffect(() => {
            setOpen(open);
          }, [open]);
          return (
            <div className={containerClassName}>
              <Disclosure.Button
                className={clsx(
                  "flex justify-between items-center bg-gray-50 text-sm font-medium py-2 px-3 !mt-0 w-full",
                  "group/accordionHeader",
                  `${
                    open ? "border-b-gray-100 border-b" : "border-b-gray-300"
                  }`,
                  headerClassName
                )}
                disabled={props.disable}
                ref={ref}
              >
                {open ? headerTitle : closeTitle ?? headerTitle}
                <div className="flex items-center">
                  {headerValue && (
                    <div className="px-2 font-medium">
                      {headerValue} {unitPower(String(unitValue ?? "")) ?? ""}
                    </div>
                  )}
                  <ChevronUpIcon
                    width={20}
                    className={clsx(
                      {
                        "rotate-90 transform": !open,
                        "opacity-40": props.disable,
                      },
                      props.arrowClassName,
                      "text-gray-700 group-hover/accordionHeader:text-gray-900"
                    )}
                  />
                </div>
              </Disclosure.Button>
              <Transition
                show={open}
                enter="transition duration-100 ease-out"
                enterFrom="transform scale-95 opacity-0"
                enterTo="transform scale-100 opacity-100"
                leave="transition duration-75 ease-out"
                leaveFrom="transform scale-100 opacity-100"
                leaveTo="transform scale-95 opacity-0"
                className={clsx("!mt-0", className)}
              >
                <Disclosure.Panel static>{children}</Disclosure.Panel>
              </Transition>
            </div>
          );
        }}
      </Disclosure>
    );
  }
);

export default Collapse;

Collapse.displayName = "Collapse";
